<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
    forms: Object,
    darkMode: { type: Boolean, default: false },
    sticky: { type: Boolean, default: false },
})

const emit = defineEmits(['toggle-dark', 'update:a11yStyle'])

const fontSizes = [
    { label: 'A', value: '14px', title: 'Normální text' },
    { label: 'A', value: '16px', title: 'Větší text' },
    { label: 'A', value: '18px', title: 'Největší text' },
]
const activeFontSize = ref('14px')
const highContrast = ref(false)

const canToggleDark = computed(() => !props.forms?.theme?.mode_lock || props.forms.theme.mode_lock === 'auto')

const a11yStyle = computed(() => ({
    fontSize: activeFontSize.value !== '14px' ? activeFontSize.value : undefined,
    filter: highContrast.value ? 'contrast(1.5) saturate(0.7)' : undefined,
}))

watch(a11yStyle, (val) => emit('update:a11yStyle', val), { immediate: true })
</script>

<template>
    <header class="rw-hdr" :class="{ 'is-sticky': sticky }">
        <div class="rw-shell rw-hdr-inner">
            <a class="rw-brand" href="#">
                <img
                    v-if="forms.theme?.show_logo !== 0 && forms.theme?.show_logo !== false && (forms.theme?.logo_url || forms.theme?.logo_url_dark)"
                    :src="darkMode && forms.theme?.logo_url_dark ? forms.theme.logo_url_dark : forms.theme?.logo_url"
                    :alt="forms.name"
                    class="rw-logo"
                />
                <span v-else class="rw-brand-name">{{ forms.name }}</span>
            </a>
            <div class="rw-hdr-right">
                <!-- A11y controls -->
                <div v-if="!sticky" class="rw-a11y">
                    <button
                        v-for="(size, i) in fontSizes"
                        :key="size.value"
                        class="rw-a11y-btn"
                        :class="[
                            { 'is-active': activeFontSize === size.value },
                            i === 0 ? 'rw-a11y-btn--xs' : i === 1 ? 'rw-a11y-btn--sm' : 'rw-a11y-btn--md'
                        ]"
                        :title="size.title"
                        @click="activeFontSize = size.value"
                    >{{ size.label }}</button>

                    <span class="rw-a11y-sep"/>

                    <button
                        class="rw-a11y-btn"
                        :class="{ 'is-active': highContrast }"
                        title="Vyšší kontrast"
                        @click="highContrast = !highContrast"
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="rw-a11y-icon">
                            <circle cx="12" cy="12" r="9"/>
                            <path d="M12 3v18M12 3a9 9 0 0 1 0 18"/>
                        </svg>
                    </button>

                    <button
                        v-if="canToggleDark"
                        class="rw-a11y-btn"
                        :class="{ 'is-active': darkMode }"
                        :title="darkMode ? 'Světlý režim' : 'Tmavý režim'"
                        @click="emit('toggle-dark')"
                    >
                        <svg v-if="darkMode" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="rw-a11y-icon">
                            <circle cx="12" cy="12" r="4"/>
                            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
                        </svg>
                        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="rw-a11y-icon">
                            <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>
                        </svg>
                    </button>
                </div>

                <span v-if="!sticky" class="rw-hdr-divider"/>

                <slot />
            </div>
        </div>
    </header>
</template>

<style scoped>
.rw-hdr {
    padding: 18px 0 0;
    position: relative;
    z-index: 2;
}

.rw-hdr.is-sticky {
    padding: 0;
    position: sticky;
    top: 0;
    z-index: 30;
    background: var(--rw-paper, #fff);
    border-bottom: 1px solid var(--rw-line-2, #EDEAE3);
}

.rw-hdr.is-sticky .rw-hdr-inner {
    padding: 14px 0;
}

.rw-shell {
    max-width: 1320px;
    margin: 0 auto;
    padding: 0 32px;
    width: 100%;
}

.rw-hdr-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.rw-brand {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
}

.rw-logo {
    height: 22px;
    display: block;
}

.rw-brand-name {
    font-family: var(--rw-font-display);
    font-weight: 700;
    font-size: 18px;
    color: var(--rw-ink);
    letter-spacing: -0.02em;
}

.rw-hdr-right {
    display: flex;
    align-items: center;
    gap: 4px;
}

/* ─── A11y controls ──────────────────────────────────────────────────────── */
.rw-a11y {
    display: flex;
    align-items: center;
    gap: 2px;
}

.rw-a11y-btn {
    width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    border-radius: var(--rw-r-sm, 8px);
    color: var(--rw-ink-3);
    cursor: pointer;
    font-family: inherit;
    font-weight: 600;
    transition: background 0.12s, color 0.12s;
}

.rw-a11y-btn:hover {
    background: rgba(15, 17, 21, 0.06);
    color: var(--rw-ink-2);
}

.rw-a11y-btn.is-active {
    background: var(--rw-brand-tint, #EFF3FC);
    color: var(--rw-brand-2, #1d5cbf);
}

.rw-a11y-btn--xs { font-size: 11px; }
.rw-a11y-btn--sm { font-size: 13px; }
.rw-a11y-btn--md { font-size: 15px; }

.rw-a11y-sep {
    width: 1px;
    height: 16px;
    background: var(--rw-line);
    margin: 0 4px;
    flex-shrink: 0;
    display: block;
}

.rw-a11y-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
}

/* ─── Slot content ───────────────────────────────────────────────────────── */
:slotted(.rw-hdr-link) {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 11px;
    border-radius: var(--rw-r-sm);
    font-size: 13px;
    font-weight: 500;
    color: var(--rw-ink-2);
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
}

:slotted(.rw-hdr-link:hover) {
    background: rgba(15, 17, 21, 0.05);
    color: var(--rw-ink);
}

:slotted(.rw-hdr-divider) {
    width: 1px;
    height: 18px;
    background: var(--rw-line);
    margin: 0 6px;
    flex-shrink: 0;
    display: block;
}

:slotted(.rw-avatar) {
    width: 32px;
    height: 32px;
    border-radius: 999px;
    background: var(--rw-ink);
    color: var(--rw-bg);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 12px;
    letter-spacing: 0.02em;
    margin-left: 6px;
    flex-shrink: 0;
}

:slotted(.rw-icon) {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
}

:slotted(.rid-close-btn) {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1px solid var(--rw-line);
    background: none;
    border-radius: var(--rw-r-md, 12px);
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    color: var(--rw-ink-2);
    cursor: pointer;
    transition: background 0.12s, color 0.12s;
}

:slotted(.rid-close-btn:hover) {
    background: var(--rw-bg);
    color: var(--rw-ink);
}

:slotted(.rid-icon) {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
}

@media (max-width: 640px) {
    .rw-shell {
        padding: 0 16px;
    }

    :slotted(.rw-hdr-link) {
        padding: 7px 8px;
    }

    :slotted(.rw-hdr-link span:last-child) {
        display: none;
    }

    .rw-a11y {
        display: none;
    }
}
</style>
